
import flatten from 'lodash/flatten';
import isEqual from 'lodash/isEqual';
import { PropType, computed, defineComponent, getCurrentInstance, toValue } from 'vue';
import { useRoute } from 'vue-router';

import ColumnsSettings from 'SharedComponents/Table/ColumnsSettings.vue';
import SBtn from 'SharedComponents/ui/Button/SBtn.vue';
import EntityLink from 'SharedComponents/ui/Link/EntityLink.vue';
import useCustomLoading from 'SharedDir/common/composables/useCustomLoading';
import { useNotifications } from 'SharedDir/common/composables/useNotifications';
import { makeRequest } from 'SharedDir/common/composables/useRequests';
import { useTableSettings } from 'SharedDir/common/composables/useTableSettings';
import { TABLE_SETTINGS } from 'SharedDir/constants/table-settings';
import {
  Computable,
  QuasarTableColumnList,
  QuasarTableExportColumn,
  QuasarTableExportExtraColumn,
} from 'SharedDir/declarations/shared';
import { resolveComputable } from 'SharedDir/utils/helpers';
import { ColumnLink, IsEntityLinkColumn } from 'SharedDir/utils/quasar';

import STableSettings from './STableSettings.vue';

function defaultTableKey() {
  const route = useRoute();
  const instance = getCurrentInstance();

  return `${route.path}_${instance?.parent?.type.name}_table`;
}

export default defineComponent({
  name: 'STable',
  data() {
    return {
      TABLE_SETTINGS,
    };
  },
  components: { EntityLink, SBtn, ColumnsSettings, STableSettings },
  props: {
    columns: {
      required: true,
      type: Array as PropType<QuasarTableColumnList>,
    },
    /**
     * Доступны ли пользователю редактирование настроек таблицы
     */
    configurable: {
      type: Boolean,
      default: true,
    },
    /**
     * Сохранять настройки таблицы в хранилище на устройстве пользователя.
     * Используется pinia store, которая по умолчанию все пишет в localStorage
     */
    saveSettings: {
      type: Boolean,
      default: true,
    },
    /**
     * Ключ, под которым настройки данной таблицы будут храниться в хранилище на устройстве
     * пользователя
     * По умолчанию будет использовано
     * ```
     *   `${[текущий путь в роутере (без параметров строки запроса)]}_${[название родительской компоненты vue]}_table`
     * ```
     */
    settingsKey: {
      type: String,
      required: false,
      default: defaultTableKey,
    },
    /**
     * Функция экспорта принимающая данные для экспорта таблицы
     * @param {QuasarTableExportColumns} data
     */
    exportFunc: {
      type: Function,
      required: false,
      default: null,
    },
    /**
     * Свойство для скрытия кнопки экспорта в случаях если
     * нужно назначить экспорт на другую кнопку вне таблицы
     */
    exportBtnIsHidden: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  expose: ['exportTable'],
  setup(props, { slots, emit, attrs }) {
    const classes = 's-table';

    const tableClasses = '';

    /**
     * Для простоты и универсальности в рамках приложения,
     * договоримся что кнопка вызова настроек будет находиться в заголовке столбца `actions`.
     *
     * Однако, поскольку большое количество таблиц уже реализовано и не у всех есть столбец с троеточием
     * мы просто пройдемся по столбцам из параметров и добавим его если у нас таблица конфигурируемая
     * @type {ComputedRef<Array<QuasarTableColumn<any>>>}
     */
    const modifiedColumns = computed(() => {
      const { columns } = props;
      const hasActions = columns.filter(({ name }) => name === 'actions').length > 0;
      if (!hasActions && props.configurable) {
        columns.push({
          name: 'actions',
          label: 'Настройки',
        });
      }
      return columns;
    });

    const {
      tableSettings,
      configuredColumns,
      resetSettings,
      makeDefaultSettings,
      hasSavedSettings,
    } = useTableSettings(modifiedColumns, props.saveSettings ? props.settingsKey : undefined);

    const resultColumns = computed(() =>
      props.configurable ? configuredColumns.value : modifiedColumns.value
    );

    const entityLinks = computed(() => {
      const obj = Object.fromEntries(
        (props.columns.filter(IsEntityLinkColumn) as ColumnLink<any>[]).map((column) => [
          `body-cell-${column.name}`,
          column.options,
        ])
      );
      // console.debug(props.columns, obj);
      return obj;
    });

    const entityLinkColumns = computed(() => Object.keys(entityLinks.value));

    /**
     * Флаг нужен для отображения кнопки сброса настроек если они не по умолчанию
     * @type {ComputedRef<boolean>}
     */
    const isCustomSettings = computed(
      () => !isEqual(toValue(tableSettings), makeDefaultSettings(modifiedColumns))
    );

    const { loading, requestOptions } = useCustomLoading();
    /**
     * Подготовка экспортируемых полей. Убираем неэскортируемые поля
     * Маппим дефолтные поля для экспорта если объекта `export` не переопределен
     * в `columns` или `export` не false.
     */
    const exportColumns = computed(() => {
      const clearColumns = resultColumns.value
        .filter((item) => item.name !== 'actions' && item?.export !== false)
        .map((item) => {
          const columnMapping = {
            name: item.label,
            extractor: item?.field && typeof item?.field === 'string' ? item?.field : item?.name,
          };
          const { export: exportConfig } = item;
          let extraColumns = [] as QuasarTableExportExtraColumn[];
          if (exportConfig) {
            extraColumns = exportConfig?.extraColumns ?? [];
          }
          if (exportConfig && typeof exportConfig === 'object') {
            return [{ ...columnMapping, ...exportConfig }, ...extraColumns];
          }
          return [{ ...columnMapping }, ...extraColumns];
        });
      return flatten(clearColumns) as QuasarTableExportColumn[];
    });

    const notify = useNotifications();

    /**
     * Функция для экспорта видимых колонок таблицы
     * Снаружи передается функция через props, в нее передается подготовленная data
     * в виде exportColumns
     */
    async function exportTable() {
      try {
        await makeRequest(async () => props.exportFunc(exportColumns.value), requestOptions);
      } catch (err) {
        notify.failureNotification('При экспорте произошла ошибка!');
      }
    }

    function resolveWithEntity<T>(
      linkLabel: Computable<string, T> | undefined,
      row: T,
      defaultLabel: string
    ) {
      if (linkLabel) {
        return resolveComputable(linkLabel, row);
      }
      return defaultLabel;
    }

    return {
      classes,
      tableClasses,
      slots,
      resultColumns,
      tableSettings,
      emit,
      resetSettings,
      hasSavedSettings,
      isCustomSettings,
      exportTable,
      loading,
      entityLinks,
      entityLinkColumns,
      resolveWithEntity,
    };
  },
});
